// Bamboo Agent Workflow - Three.js + React Conversion with Visual + CAD Logic
import React, {
  useEffect,
  useRef,
  useState,
  useCallback,
  useMemo,
} from "react";
import * as THREE from "three";
import { STLLoader } from "three/examples/jsm/loaders/STLLoader";
import { STLExporter } from "three/examples/jsm/exporters/STLExporter";
import { initializeThreeScene } from "../utils/initializeThreeScene";
import useVisualHelpers from "../hooks/useVisualHelpers";
import { useSyncedCamera } from "../hooks/useSyncedCamera";
import TutorialWindow from "../components/TutorialWindow";
import { bambooAgentTutorial } from "../utils/tutorialContents";
import "../styles/common.css";
import ConstraintWarning from "../components/ConstraintWarning";
import { checkPrinterFit, onLabConstraintsChange } from "../utils/constraints";

const BambooAgent = ({ stlFile }) => {
  const containerRef = useRef(null);
  const sceneRef = useRef(null);
  const rendererRef = useRef(null);
  const cameraRef = useRef(null);
  const controlsRef = useRef(null);
  const objRef = useRef(null);
  const hollowRef = useRef(null);

  const [constraintWarnings, setConstraintWarnings] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [modelDimensions, setModelDimensions] = useState(null);
  const [tutorialVisible, setTutorialVisible] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [faceCountError, setFaceCountError] = useState(null);
  const [faceCount, setFaceCount] = useState(0);

  const { createVisualization, removeVisualHelpers } = useVisualHelpers();
  const pageId = useMemo(() => "bamboo-agent", []);
  useSyncedCamera(pageId, cameraRef, controlsRef);

  const fastIterNotRecommended = faceCount > 60 || constraintWarnings.length > 0;

  const initScene = useCallback(() => {
    if (containerRef.current) {
      while (containerRef.current.firstChild) {
        containerRef.current.removeChild(containerRef.current.firstChild);
      }
    }

    const cleanup = initializeThreeScene(
      containerRef,
      sceneRef,
      rendererRef,
      cameraRef,
      controlsRef
    );

    if (sceneRef.current) {
      const grid = new THREE.GridHelper(200, 20, 0x444444, 0x888888);
      grid.material.opacity = 0.4;
      grid.material.transparent = true;
      sceneRef.current.add(grid);
      sceneRef.current.add(new THREE.AxesHelper(50));

      const hemi = new THREE.HemisphereLight(0xffffff, 0xbfd4d2, 0.9);
      sceneRef.current.add(hemi);

      const dirLight = new THREE.DirectionalLight(0xffffff, 0.3);
      dirLight.position.set(15, 30, 22.5);
      dirLight.castShadow = true;
      dirLight.shadow.mapSize.setScalar(2048);
      dirLight.shadow.bias = -1e-4;
      dirLight.shadow.normalBias = 1e-4;
      sceneRef.current.add(dirLight);

      const shadowPlane = new THREE.Mesh(
        new THREE.PlaneGeometry(1000, 1000),
        new THREE.ShadowMaterial({ transparent: true, opacity: 0.075 })
      );
      shadowPlane.rotation.x = -Math.PI / 2;
      shadowPlane.receiveShadow = true;
      sceneRef.current.add(shadowPlane);
    }

    const handleResize = () => {
      if (cameraRef.current && rendererRef.current && containerRef.current) {
        const width = containerRef.current.clientWidth;
        const height = containerRef.current.clientHeight;
        cameraRef.current.aspect = width / height;
        cameraRef.current.updateProjectionMatrix();
        rendererRef.current.setSize(width, height);
      }
    };
    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
      if (cleanup) cleanup();
    };
  }, []);

  const checkFaceCount = useCallback((geometry) => {
    const MAX_FACES = 100;
    let faces = 0;

    if (geometry.index) {
      faces = geometry.index.count / 3;
    } else {
      faces = geometry.attributes.position.count / 3;
    }

    setFaceCount(Math.floor(faces));

    if (faces > MAX_FACES) {
      setFaceCountError(
        `The model has too many faces (${Math.floor(
          faces
        )} faces), exceeding the maximum limit of ${MAX_FACES} faces. Please use a model with fewer faces.`
      );
      return false;
    } else {
      setFaceCountError(null);
      return true;
    }
  }, []);

  const createHollowTriangles = useCallback(
    (geometry, yOffset) => {
      // face count guard
      if (!checkFaceCount(geometry)) return;

      const scene = sceneRef.current;
      const group = new THREE.Group();
      const pos = geometry.attributes.position.array;
      const norm = geometry.attributes.normal.array;

      for (let i = 0; i < pos.length; i += 9) {
        const v1 = new THREE.Vector3(pos[i], pos[i + 1], pos[i + 2]);
        const v2 = new THREE.Vector3(pos[i + 3], pos[i + 4], pos[i + 5]);
        const v3 = new THREE.Vector3(pos[i + 6], pos[i + 7], pos[i + 8]);
        const normal = new THREE.Vector3(norm[i], norm[i + 1], norm[i + 2]);
        const centroid = new THREE.Vector3()
          .add(v1)
          .add(v2)
          .add(v3)
          .divideScalar(3);

        const innerV1 = new THREE.Vector3().lerpVectors(v1, centroid, 0.3);
        const innerV2 = new THREE.Vector3().lerpVectors(v2, centroid, 0.3);
        const innerV3 = new THREE.Vector3().lerpVectors(v3, centroid, 0.3);

        const depth = -1.5;
        const extrude = (v) =>
          v.clone().add(normal.clone().multiplyScalar(depth));

        const vertices = [
          v1, v2, v3,
          innerV1, innerV2, innerV3,
          extrude(v1), extrude(v2), extrude(v3),
          extrude(innerV1), extrude(innerV2), extrude(innerV3),
        ];

        const idx = [
          0,1,6, 1,7,6, 1,2,7, 2,8,7, 2,0,8, 0,6,8,
          3,4,9, 4,10,9, 4,5,10, 5,11,10, 5,3,11, 3,9,11,
          0,3,6, 6,3,9, 1,4,7, 7,4,10, 2,5,8, 8,5,11,
          0,1,3, 1,4,3, 1,2,4, 2,5,4, 2,0,5, 0,3,5,
          6,7,9, 7,10,9, 7,8,10, 8,11,10, 8,6,11, 6,9,11,
        ];

        const bufGeo = new THREE.BufferGeometry();
        const posArr = new Float32Array(vertices.flatMap((v) => [v.x, v.y, v.z]));
        bufGeo.setAttribute("position", new THREE.BufferAttribute(posArr, 3));
        bufGeo.setIndex(idx);
        bufGeo.computeVertexNormals();

        const mesh = new THREE.Mesh(
          bufGeo,
          new THREE.MeshPhongMaterial({
            color: new THREE.Color().setHSL(Math.random(), 1, 0.5),
            side: THREE.DoubleSide,
            transparent: false,
            opacity: 0.8,
          })
        );
        group.add(mesh);
      }

      // align to grid plane
      group.position.y -= yOffset;

      hollowRef.current = group;
      scene.add(group);
    },
    [checkFaceCount]
  );

  // unified fit check
  const runFitCheck = useCallback(() => {
    if (!hollowRef.current) return;
    const warning = checkPrinterFit(hollowRef.current);
    setConstraintWarnings(warning ? [warning] : []);
    return !warning; // true if fits
  }, []);

  const exportHollowTriangles = useCallback(() => {
    if (!hollowRef.current) {
      if (faceCountError) {
        alert(faceCountError);
        return;
      }
      alert("请先生成模型");
      return;
    }

    // block export if it doesn’t fit
    const isValid = runFitCheck();
    if (!isValid) return;

    const exporter = new STLExporter();
    const result = exporter.parse(hollowRef.current);
    const blob = new Blob([result], { type: "text/plain" });
    const url = URL.createObjectURL(blob);

    const link = document.createElement("a");
    link.href = url;
    link.download = "bamboo_agent_triangles.stl";
    document.body.appendChild(link);
    link.click();

    setTimeout(() => {
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    }, 100);
  }, [runFitCheck, faceCountError]);

  const loadModel = useCallback(
    (filePath) => {
      const loader = new STLLoader();
      const path = filePath || "/models/twistT.stl";

      loader.load(
        path,
        (geometry) => {
          const material = new THREE.MeshNormalMaterial({
            transparent: true,
            opacity: 0.3,
          });

          // center + orient like other pages
          const bbox = new THREE.Box3().setFromBufferAttribute(
            geometry.getAttribute("position")
          );
          const center = new THREE.Vector3();
          bbox.getCenter(center);
          geometry.translate(-center.x, -center.y, -center.z);
          geometry.rotateX(-Math.PI / 2);
          geometry.rotateY(-Math.PI / 2);

          const mesh = new THREE.Mesh(geometry, material);

          const finalBox = new THREE.Box3().setFromBufferAttribute(
            geometry.getAttribute("position")
          );
          const size = new THREE.Vector3();
          finalBox.getSize(size);

          const yOffset = finalBox.min.y;
          mesh.position.y -= yOffset;

          objRef.current = mesh;
          sceneRef.current.add(mesh);

          createVisualization(mesh, sceneRef.current, finalBox);
          setModelDimensions({ width: size.x, height: size.y, depth: size.z });

          // initial fit warning (standardized)
          const warning = checkPrinterFit(mesh);
          setConstraintWarnings(warning ? [warning] : []);

          // create hollow geometry after face-count check
          const isValidFaceCount = checkFaceCount(geometry);
          if (isValidFaceCount) {
            createHollowTriangles(geometry, yOffset);
          }
          setIsLoading(false);
        },
        undefined,
        (err) => {
          console.error("Error loading STL:", err);
          setError("Failed to load STL model.");
          setIsLoading(false);
        }
      );
    },
    [createVisualization, createHollowTriangles, checkFaceCount]
  );

  // mount
  useEffect(() => {
    const cleanup = initScene();
    loadModel(stlFile);
    return () => {
      if (cleanup) cleanup();
      if (sceneRef.current) {
        removeVisualHelpers(sceneRef.current);
        sceneRef.current.clear();
      }
    };
  }, [initScene, loadModel, stlFile, removeVisualHelpers]);

  // re-check a moment after hollow is created
  useEffect(() => {
    const timer = setTimeout(() => {
      runFitCheck();
    }, 500);
    return () => clearTimeout(timer);
  }, [runFitCheck]);

  // re-check when Sidebar machine changes
  useEffect(() => {
    const off = onLabConstraintsChange(() => {
      runFitCheck();
    });
    return off;
  }, [runFitCheck]);

  const toggleTutorial = useCallback(() => {
    setTutorialVisible((prev) => !prev);
  }, []);

  return (
    <div className="container">
      <button
        className="panel-toggle-button"
        onClick={() => setExpanded(!expanded)}
      >
        {expanded ? "⟨ Hide Panel" : "⟩ Show Panel"}
      </button>

      <div ref={containerRef} className="canvas-container"></div>

      {constraintWarnings.length > 0 && (
        <div className="alerts-stack">
          {constraintWarnings.map((warning, index) => (
            <ConstraintWarning
              key={index}
              warning={warning}
              onClose={() =>
                setConstraintWarnings((prev) => prev.filter((_, i) => i !== index))
              }
            />
          ))}
        </div>
      )}

      {faceCountError && (
        <div
          style={{
            position: "fixed",
            top: "80px",
            left: "50%",
            transform: "translateX(-50%)",
            zIndex: 1001,
            backgroundColor: "#ff4444",
            color: "white",
            padding: "12px 20px",
            borderRadius: "6px",
            boxShadow: "0 2px 10px rgba(0,0,0,0.3)",
            maxWidth: "90vw",
            textAlign: "center",
            fontSize: "14px",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <span style={{ fontSize: "20px", marginRight: "10px" }}>⚠️</span>
            <div>
              <strong>Model Face Count Exceeded</strong>
              <br />
              {faceCountError}
            </div>
            <button
              onClick={() => setFaceCountError(null)}
              style={{
                marginLeft: "15px",
                background: "none",
                border: "none",
                color: "white",
                fontSize: "18px",
                cursor: "pointer",
              }}
            >
              ×
            </button>
          </div>
        </div>
      )}

      {!isLoading && expanded && (
        <div className="controls-panel">
          <div className="panel-title">Bamboo Agent</div>

          <div className="panel-section">
            <div className="section-header">
              <h4>Workflow Description</h4>
            </div>
            <div className="section-content">
              <p>
                Based on the workflow from Bamboo Agents: Exploring the
                Potentiality of Digital Craft by Decoding and Recoding Process,
                this tool uses 3D printing to create a base structure that
                supports subsequent fabrication processes. Users can visualize
                an imported model and explore how it can be transformed into a
                3D-printed foundation.
              </p>
              <p style={{ fontStyle: "italic" }}>
                Use it for fabrication prototypes and structural study models.
              </p>
            </div>
          </div>

          <div className="panel-section">
            <div
              className="section-header"
              style={{ display: "flex", alignItems: "center", gap: 8 }}
            >
              <h4>Time & Effort Evaluation</h4>
              {fastIterNotRecommended && (
                <span
                  style={{
                    fontSize: 12,
                    padding: "2px 8px",
                    borderRadius: 999,
                    background: "#ff9800",
                    color: "#fff",
                    whiteSpace: "nowrap",
                  }}
                  title="High manual work, not ideal for quick iteration"
                >
                  Not Ideal for Quick Iteration
                </span>
              )}
            </div>
            <div className="section-content">
              <ul style={{ marginLeft: 16, lineHeight: 1.5 }}>
                <li>
                  This workflow involves <strong>significant manual labor</strong>.
                  Total build time can be hard to estimate.
                </li>
                <li>
                  If your goal is <strong>fast iteration</strong>, this method is{" "}
                  <strong>not recommended</strong>. Consider faster alternatives like
                  stacked laser cutting, foam cutting, or low-res FDM prints for early tests.
                </li>
                <li>
                  Best suited for: form exploration, structural study models, or
                  prototypes that require a woven/paper-mâché aesthetic.
                </li>
              </ul>
            </div>
          </div>

          <div className="buttons-container">
            <button
              className="action-button"
              onClick={exportHollowTriangles}
              disabled={constraintWarnings.length > 0 || !!faceCountError}
            >
              EXPORT STL FILE
            </button>
            <button className="action-button" onClick={toggleTutorial}>
              {tutorialVisible
                ? "HIDE INTERACTIVE GUIDANCE"
                : "SHOW INTERACTIVE GUIDANCE"}
            </button>
          </div>
        </div>
      )}

      {isLoading && <div className="loading-box">Loading model...</div>}
      {error && <div className="error-box">Error: {error}</div>}

      <TutorialWindow
        visible={tutorialVisible}
        onToggleVisibility={toggleTutorial}
        tutorialContent={bambooAgentTutorial}
      />
    </div>
  );
};

export default BambooAgent;
